import { useEffect, useState } from 'react';
import { Anchor, Badge, Box, Button, Group, Loader, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { IconAlertTriangle, IconCloudCheck, IconCloudOff, IconClockPause } from '@tabler/icons-react';
import { listCardOps, onCardOps, retryCardOp } from '../offline/card-ops';
import type { PendingCardOp } from '../offline/db';
import {
  flushOutbox,
  getSyncStatus,
  listRejected,
  onSyncStatus,
  recheckOnline,
  retryStuckOutbox,
  type RejectedOperation,
  type SyncStatus,
} from '../offline/sync';
import { dataPermissionHint } from '../api/platform';

/**
 * Состояние связи и очереди отправки.
 *
 * Замерщику важно видеть не «есть Wi-Fi», а «мои замеры доехали». Пока в
 * очереди что-то есть, индикатор остаётся заметным — иначе человек уедет
 * с объекта, считая работу сданной. Отклонённые сервером операции не
 * ретраятся молча вечно — они показаны отдельно и требуют внимания.
 */
export function SyncIndicator() {
  const [status, setStatus] = useState<SyncStatus>(getSyncStatus());

  useEffect(() => onSyncStatus(setStatus), []);
  useCardOpsNews();

  if (status.stuckCount + status.cardsRefused > 0) {
    return <RejectedBadge count={status.stuckCount + status.cardsRefused} />;
  }
  return (
    <Group gap={6} wrap="nowrap">
      <StateBadge status={status} />
      {status.foreign > 0 && (
        <Tooltip label="Несданные замеры другого сотрудника на этом планшете — уйдут, когда он войдёт" withArrow multiline w={260}>
          <Badge color="gray" variant="light" data-testid="sync-foreign">
            <Box component="span" visibleFrom="sm">
              чужих:{' '}
            </Box>
            {status.foreign}
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

/**
 * ОТКЛОНЁННОЕ — С ПРИЧИНОЙ, А НЕ ПОДСКАЗКОЙ ПРИ НАВЕДЕНИИ. У планшета нет
 * наведения: причина жила в подсказке, а нажатие сразу повторяло отправку, и
 * замерщик так и не узнавал, что поправить (правило 179). Теперь нажатие
 * открывает список — адрес и слова сервера по каждому замеру, — а повтор
 * стоит кнопкой под ним.
 */
function RejectedBadge({ count }: { count: number }) {
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState<RejectedOperation[] | null>(null);
  const [cards, setCards] = useState<PendingCardOp[]>([]);
  useEffect(() => {
    if (!opened) return;
    let alive = true;
    void listRejected().then((list) => {
      if (alive) setItems(list);
    });
    void listCardOps().then((ops) => {
      if (alive) setCards(ops.filter((o) => o.refusedAt !== null));
    });
    return () => {
      alive = false;
    };
  }, [opened, count]);
  return (
    <Popover opened={opened} onChange={setOpened} width={300} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Badge
          color="red"
          variant="filled"
          leftSection={<IconAlertTriangle size={14} />}
          style={{ cursor: 'pointer' }}
          onClick={() => setOpened((o) => !o)}
          data-testid="sync-rejected"
        >
          <Box component="span" visibleFrom="sm">
            Не отправлено:{' '}
          </Box>
          {count}
        </Badge>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6} data-testid="sync-rejected-list">
          <Text size="sm" fw={600}>
            Сервер не принял — поправьте и повторите
          </Text>
          {items === null && <Loader size="xs" />}
          {items?.map((o) => (
            <Text key={o.clientMutationId} size="xs">
              <b>{o.address ?? 'Замер'}</b>: {o.error ?? 'сервер отклонил без причины'}
            </Text>
          ))}
          {/* Расходки без связи — с причиной; поправить набранное можно в «Расходке» */}
          {cards.map((o) => (
            <Text key={o.id} size="xs" data-testid="sync-refused-card">
              <b>Расходка {o.label}</b>: {o.lastError ?? 'сервер отклонил без причины'}
            </Text>
          ))}
          {cards.length > 0 && (
            <Anchor component={Link} to="/consumables" size="xs" onClick={() => setOpened(false)}>
              Открыть расходки — поправить или убрать
            </Anchor>
          )}
          <Button
            size="xs"
            onClick={() => {
              setOpened(false);
              void retryStuckOutbox();
              // Правку выданной повтор не отправит никогда — её убирают в «Расходке»
              cards.filter((o) => !o.final).forEach((o) => void retryCardOp(o.id));
            }}
          >
            Повторить отправку
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function StateBadge({ status }: { status: SyncStatus }) {

  if (status.syncing) {
    return (
      <Group gap={6}>
        <Loader size="xs" />
        <span style={{ fontSize: 13 }}>Отправка…</span>
      </Group>
    );
  }

  const waiting = status.pending + status.photos + status.cards;
  if (waiting > 0) {
    const parts = [
      status.pending > 0 ? `замеров: ${status.pending}` : null,
      status.photos > 0 ? `листов с холста: ${status.photos}` : null,
      status.cards > 0 ? `расходок: ${status.cards}` : null,
    ].filter(Boolean);
    return (
      <Tooltip
        label={`Ждут связи — ${parts.join(', ')}. Уйдут сами; нажмите, чтобы отправить сейчас`}
        withArrow
      >
        <Badge
          color="orange"
          variant="filled"
          leftSection={<IconClockPause size={14} />}
          style={{ cursor: 'pointer' }}
          onClick={() => void flushOutbox()}
        >
          <Box component="span" visibleFrom="sm">
            Ждут отправки:{' '}
          </Box>
          {waiting}
        </Badge>
      </Tooltip>
    );
  }

  if (!status.online) return <OfflineBadge />;

  return (
    <Tooltip
      label={status.lastSyncAt ? `Синхронизировано ${new Date(status.lastSyncAt).toLocaleTimeString('ru-RU')}` : 'Связь есть'}
      withArrow
    >
      <Badge color="teal" variant="light" leftSection={<IconCloudCheck size={14} />}>
        <Box component="span" visibleFrom="sm">
          Синхронизировано
        </Box>
      </Badge>
    </Tooltip>
  );
}

/**
 * «ОФФЛАЙН» НАЖИМАЕТСЯ (12.10.2026): значок, оставшийся от пропавшего
 * интернета, человек проверяет сам, не перезапуская приложение, — и слышит
 * ответ. Сервер молчит при живом интернете — значит, связь не доходит до
 * него: приложению могли запретить мобильные данные или Wi‑Fi в настройках
 * телефона (на Honor и Xiaomi такой вопрос всплывает один раз), и туда
 * подсказка и ведёт — отказ, из которого не видно, что делать, это
 * половина отказа (правило 179).
 */
function OfflineBadge() {
  const [checking, setChecking] = useState(false);
  const check = async () => {
    if (checking) return;
    setChecking(true);
    const ok = await recheckOnline();
    setChecking(false);
    if (ok) {
      notifications.show({ color: 'teal', message: 'Связь с сервером есть' });
      void flushOutbox();
    } else {
      notifications.show({
        color: 'orange',
        autoClose: 15000,
        message:
          'Сервер не отвечает. Проверьте интернет на телефоне. Если интернет есть, а сюда не пускает — ' +
          dataPermissionHint(),
      });
    }
  };
  return (
    <Tooltip label="Нажмите, чтобы проверить связь" withArrow>
      <Badge
        color="gray"
        variant="light"
        leftSection={checking ? <Loader size={10} /> : <IconCloudOff size={14} />}
        style={{ cursor: 'pointer' }}
        onClick={() => void check()}
        data-testid="sync-offline"
      >
        Оффлайн
      </Badge>
    </Tooltip>
  );
}

/**
 * РАСХОДКА БЕЗ СВЯЗИ УШЛА — СКАЗАНО (07.10.2026). Человек нажал «Отправить»
 * в подвале и ушёл работать; о том, что она доехала до склада или что
 * сервер отказал, он узнаёт сам, на любом экране, а не открыв расходки.
 * Списки и карточки при этом перечитываются: склад и бригада увидят её.
 */
function useCardOpsNews() {
  const qc = useQueryClient();
  useEffect(
    () =>
      onCardOps((event) => {
        if (event.type === 'sent') {
          void qc.invalidateQueries({ queryKey: ['consumables-cards'] });
          void qc.invalidateQueries({ queryKey: ['consumables-jobs'] });
          void qc.invalidateQueries({ queryKey: ['consumables-card', event.op.cardId] });
          const number = event.number ? ` №${event.number}` : '';
          notifications.show({
            color: 'teal',
            message:
              event.op.kind === 'create'
                ? `Расходка${number}, сохранённая без связи, ушла ${event.op.submit ? 'на склад' : 'на сервер черновиком'}`
                : `Правка расходки${number}, сохранённая без связи, ушла${event.op.submit ? ' — расходка на складе' : ''}`,
          });
          if (event.conflicts.length > 0) {
            notifications.show({
              color: 'orange',
              autoClose: false,
              message:
                `Расходку${number} правили, пока не было связи, — правки сведены. Где вы правили одно и то же, осталось ваше: ` +
                event.conflicts.slice(0, 6).join(', '),
            });
          }
        } else if (event.type === 'refused') {
          notifications.show({
            color: 'red',
            autoClose: false,
            message: `Расходка ${event.op.label} не ушла: ${event.error}. Откройте «Расходку» — набранное сохранено`,
          });
        }
      }),
    [qc],
  );
}
